import { autoinject, bindable, computedFrom, transient } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { VettingService } from "../../../../../../services/vetting/vetting.service";
import {
    VetClientModel,
    SubtaskCorrespondenceEmailModel,
    EmailRecipientTypeEnum
} from "../../../../../../models/vetting/vetting.models";
import {
    NotificationClientService,
    NotificationType
} from "../../../../../../services/basics/notification-client.service";
import {
    EntityValidationsService,
    ClientSideValidator
} from "../../../../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum } from "../../../../../../client-validation-system/validations.models";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { CargoTypeCategoryEnum, VesselDetailModel } from "../../../../../../models/vessels/vessel.models";
import { Reflection } from "../../../../../../utilities/reflection";

@autoinject
@transient()
export class ChangeShipManagerEmailCustomDialog {

    shipMangerEmails: SubtaskCorrespondenceEmailModel[] = [{
        email: "",
        canEdit: true,
        emailRecipientTypeId: EmailRecipientTypeEnum.To,
        isShipManagerEmail: true,
        isFromConfig: false
    }];
    previouslyUsedShipManagerEmail: { email: string; selected: boolean }[] = [];
    docVettingContactEmails: { email: string; selected?: boolean, isPrimary:boolean }[] = [];
    vetId: number;
    vpqEmail: string;
    vpqEmailSelected: boolean;
    docEmail: string;
    docEmailSelected: boolean;
    validatorShipManagerEmails: ClientSideValidator;
    onConfirm: () => void;

    setValidator() {
        const rulesHolderEmails = {
            activation: ValidationActivation.Always,
            entityName: "Vet Request Ship Manager Emails",
            isCollection: true,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Ship manager's email address is required",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Ship manager's email address is not in correct format",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Email
            }]
        };
        this.validatorShipManagerEmails = this.entityValidationsService.getValidator(this.shipMangerEmails, rulesHolderEmails);
    }

    constructor(private dialogClientService: DialogClientService, private vettingService: VettingService, private notificationService: NotificationClientService,
        private entityValidationsService: EntityValidationsService) {
    }

    @bindable
    model: { onConfirm?: () => void, vet?: VetClientModel, vessel: VesselDetailModel };

    async attached() {
        if (this.model && this.model.vet) {
            this.vetId = this.model.vet.vetId;
            this.shipMangerEmails = this.model.vet.initialEmailsForCorrespondence.filter(x => x.isShipManagerEmail);
            const previouslyUsedShipManagerEmail = await this.vettingService.getPreviouslyUsedShipManagerEmail(this.model.vet.vetId);
            this.previouslyUsedShipManagerEmail = previouslyUsedShipManagerEmail.previousVetEmails.map(x => {
                return {
                    email: x,
                    selected: false
                };
            });
            this.docVettingContactEmails = previouslyUsedShipManagerEmail.docVettingContactEmails;
            this.vpqEmail = previouslyUsedShipManagerEmail.vpqEmail;
            this.docEmail = previouslyUsedShipManagerEmail.docEmail;
        }

        if (this.shipMangerEmails.length == 0)
            this.addEmail(false);

    }

    @computedFrom(Reflection.getPropertyPath<ChangeShipManagerEmailCustomDialog>(z => z.model),
        Reflection.getPropertyPath<ChangeShipManagerEmailCustomDialog>(z => z.model.vessel),
        Reflection.getPropertyPath<ChangeShipManagerEmailCustomDialog>(z => z.model.vessel.cargoTypeCategoryId))
    get dryVessel(){
        if(this.model && this.model.vessel){
            return this.model.vessel.cargoTypeCategoryId == CargoTypeCategoryEnum.Dry;
        }
        else {
            return true;
        }
    }

    addSelectedEmails() {
        for (const e of this.previouslyUsedShipManagerEmail) {
            if (e.selected) {
                this.addEmail(true, e.email);
                e.selected = false;
            }
        }
    }

    addSelectedDocVettingContactEmails() {
        for (const e of this.docVettingContactEmails) {
            if (e.selected) {
                this.addEmail(true, e.email);
                e.selected = false;
            }
        }
    }

    addSelectedVpqEmail() {
        if (this.vpqEmailSelected) {
            this.addEmail(true, this.vpqEmail);
            this.vpqEmailSelected = false;
        }
    }

    addSelectedDocEmail() {
        if (this.docEmailSelected) {
            this.addEmail(true, this.docEmail);
            this.docEmailSelected = false;
        }
    }

    close() {
        if (this.model && this.model.onConfirm)
            this.model.onConfirm();

        this.dialogClientService.hide();
    }

    navigateToVet() {
        this.dialogClientService.hide();
    }

    removeEmail(i) {
        if (i == 0)
            this.shipMangerEmails[i].email = "";
        else
            this.shipMangerEmails.splice(i, 1);
    }

    addEmail(canEdit: boolean = true, email: string = "") {
        if(email){
            const toRemove = this.shipMangerEmails.filter(x => !x.email);
            for(const r of toRemove)
                this.shipMangerEmails.splice(this.shipMangerEmails.indexOf(r), 1);
        }

        this.shipMangerEmails.push({
            email: email,
            canEdit: canEdit,
            emailRecipientTypeId: EmailRecipientTypeEnum.To,
            isShipManagerEmail: true,
            isFromConfig: false
        });
    }

    async submit() {
        this.setValidator();
        const validator = await this.validatorShipManagerEmails.validate();
        if (validator.valid) {
            try {
                const vetCorrespondenceEmails = this.model.vet.initialEmailsForCorrespondence
                    .filter(x => !x.isShipManagerEmail)
                    .concat(this.shipMangerEmails);

                await this.vettingService.saveVetCorrespondenceEmails(this.model.vet.vetId, vetCorrespondenceEmails);
                this.close();
            } catch (e) {
                if (!RequestClientService.isExceptionHandled(e)) {
                    this.notificationService.show("Error confirming contact details", "", NotificationType.Error);
                }
            }
        }
    }

    detached() {
        if (this.validatorShipManagerEmails && this.validatorShipManagerEmails.dispose)
            this.validatorShipManagerEmails.dispose();
    }
}
